import React, { useEffect } from 'react';
import { isActive } from 'Classes/Household';
import MetaControl from 'Components/UI/Meta/MetaControl';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useHouseholdResource from 'Hooks/useHouseholdResource';
import { makeStyles } from '@material-ui/core/styles';
import numeral from 'numeral';
import classnames from 'classnames';
import PropTypes from 'prop-types';


const MONTHLY = 'Monthly';
const QUARTERLY = 'Quarterly';
const SEMI_ANNUALLY = 'Semi-Annual';
const ANNUALLY = 'Annual';

const getBillingFrequencyText = (interval, intervalCount) => {
  const FREQUENCY_PLACEHOLDER = `(${intervalCount}) ${interval}`;

  if (interval === 'month') {
    switch (intervalCount) {
      case '1':
        return MONTHLY;
      case '3':
        return QUARTERLY;
      case '6':
        return SEMI_ANNUALLY;
      default:
        return FREQUENCY_PLACEHOLDER;
    }
  }

  if (interval === 'year' && intervalCount === '1') return ANNUALLY;

  if (!interval || !intervalCount) return '';

  return FREQUENCY_PLACEHOLDER;
};

const styles = makeStyles(theme => ({
  subsectionTitle: {
    color: theme.palette.facet.primaryBlue,
    fontWeight: theme.typography.fontWeightFacetBook,
    fontSize: theme.typography.fontSize1Pt25Rem,
  },
  text: {
    fontSize: theme.typography.fontSize1Rem,
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightBold,
  },
  nextPaymentSubtitle: {
    fontSize: theme.typography.fontSizePt75Rem,
  },
  flexRow: {
    display: theme.flexbox.displayFlex,
    justifyContent: theme.flexbox.justifyContentSpaceBetween,
  },
  roundedGreyBox: {
    padding: theme.spacing(1.5, 3),
    backgroundColor: theme.palette.facet.backgroundGray,
    borderRadius: theme.radius.md,
    marginBottom: '.5rem',
  },
  paddingBottom: {
    paddingBottom: '1.5rem',
  },
  fullwidth: {
    width: '100%',
  },
  creditCardTerminalButton: {
    backgroundColor: theme.palette.facet.accentBlue,
  },
}));

const HouseholdSectionBillingServiceLevelSummary = ({ householdResource }) => {
  const classes = styles();
  const householdContext = useSimpleHousehold();

  const { interval, intervalCount } = householdContext.household;

  // NOTE: We will probably display this again in the future, but it won't come from the app anymore since
  // in-app discounts became obsolete with Netsuite.  When the server is able to get a list of discounts from
  // Netsuite, we can revisit this.
  // const [discounts, setDiscounts] = useState([]);

  const { resource, setResourceDeprecated } = useHouseholdResource(householdResource);

  const billingFrequency = getBillingFrequencyText(interval, intervalCount);

  const viewServiceLevel = () => {
    let serviceLevel = 'N/A';

    if (householdResource.attr('serviceLevel')) {
      serviceLevel = numeral(householdResource.attr('serviceLevel') / 100).format('$0,0');
    }

    return (
      <div className={classnames(classes.flexRow, classes.paddingBottom)}>
        <div className={classes.subsectionTitle}>Service Level</div>
        <div className={classes.text}>{serviceLevel}</div>
      </div>
    );
  };

  const viewPaymentFrequency = () => {
    return (
      <div className={classnames(classes.flexRow, classes.paddingBottom)}>
        <div className={classes.subsectionTitle}>Frequency</div>
        <div className={classes.text}>{billingFrequency || 'N/A'}</div>
      </div>
    );
  };

  const viewPaymentMethod = () => {
    return (
      <>
        <MetaControl
          className={classes.fullwidth}
          attrName="paymentMethod"
          disabled={!isActive(householdContext.household)}
          onChange={(value) => {
            const householdResourceCopy = resource.copy();

            householdResourceCopy.attr('paymentMethod', value);

            return householdContext
              .updateHousehold(householdResourceCopy.export())
              .then(updatedResource => setResourceDeprecated(updatedResource.copy()));
          }}
          resource={resource}
        />
      </>
    );
  };

  const viewCoupons = () => {
    // NOTE: We will probably display this again in the future, but it won't come from the app anymore since
    // in-app discounts became obsolete with Netsuite.  When the server is able to get a list of discounts from
    // Netsuite, we can revisit this.
    // return (
    //   <div className={classes.paddingBottom}>
    //     <div className={classes.subsectionTitle}>Coupons & Promotions</div>
    //     <div className={classes.text}>
    //       <DiscountList discounts={discounts} />
    //       {discounts.length === 0 ? <p>No coupons or promotions at this time</p> : <></>}
    //     </div>
    //   </div>
    // );
    return null;
  };

  useEffect(() => {

    // NOTE: We will probably display this again in the future, but it won't come from the app anymore since
    // in-app discounts became obsolete with Netsuite.  When the server is able to get a list of discounts from
    // Netsuite, we can revisit this.
    // if (
    //   resource
    //   && resource.relationship('discounts')
    //   && resource.relationship('discounts').link('related')
    // ) {
    //   resource
    //     .relationship('discounts')
    //     .link('related')
    //     .Get()
    //     .then(res => setDiscounts(res.resources().list()))
    //     .catch(err => displayErrorMessage(err, 'Failed to retrieve discounts'));
    // }
  }, [resource]);

  return (
    <div className={classes.roundedGreyBox}>
      {viewServiceLevel()}
      {viewCoupons()}
      {viewPaymentFrequency()}
      {viewPaymentMethod()}
    </div>
  );
};
HouseholdSectionBillingServiceLevelSummary.propTypes = {
  householdResource: PropTypes.object.isRequired,
};

export default HouseholdSectionBillingServiceLevelSummary;
